// @flow

import { type DeviceType } from '@chatchamp/chatchamp-types'

import { getItem } from '../../../../common'
import { isUrlMatching } from '../../../../common/utils'

import {
  DEPRECATED_SHOWN_WELCOME_MESSAGE_IDS_KEY,
  SHOWN_WELCOME_MESSAGE_IDS_KEY,
  TRIGGERS,
  type WelcomeMessage
} from '../../../models'

// only exported for spec
export function checkDevice (wm: WelcomeMessage, device: DeviceType): boolean {
  if (wm.type === 'open_chat' && device === 'mobile') return false
  return (!device || !wm.device || wm.device === 'any' || wm.device === device)
}

// only exported for spec
export function isWelcomeMessageSuitable (wm: WelcomeMessage, url: string, device: DeviceType = 'any'): boolean {
  return isUrlMatchingWrapper(wm, url) && checkDevice(wm, device)
}

export function alreadySeenWelcomeMessageIds (): Array<string> {
  return (getItem(SHOWN_WELCOME_MESSAGE_IDS_KEY) || getItem(DEPRECATED_SHOWN_WELCOME_MESSAGE_IDS_KEY) || '').split(',')
}

export function getWelcomeMessage (welcomeMessages: Array<WelcomeMessage>, url: string, device: DeviceType, welcomeMessagePayload: ?string): ?WelcomeMessage {
  if (welcomeMessagePayload) return _getWelcomeMessageByPayload(welcomeMessages, welcomeMessagePayload)

  const activeWelcomeMessages = welcomeMessages.filter(wm => wm.active)
  return getAlreadyShownWelcomeMessage(activeWelcomeMessages, url, device) ||
    getAbTestedWelcomeMessage(activeWelcomeMessages.filter(wm => !!wm.regex), url, device) ||
    getAbTestedWelcomeMessage(activeWelcomeMessages, url, device) ||
    getDefaultWelcomeMessage(activeWelcomeMessages)
}

function _getWelcomeMessageByPayload (welcomeMessages: Array<WelcomeMessage>, welcomeMessagePayload: ?string): ?WelcomeMessage {
  if (!welcomeMessagePayload) return

  return welcomeMessages.find(wm => wm.payload === welcomeMessagePayload)
}

// only exported for spec
export function getAlreadyShownWelcomeMessage (welcomeMessages: Array<WelcomeMessage>, url: string, device: DeviceType): ?WelcomeMessage {
  const availableWelcomeMessages = welcomeMessages
    .filter(wm => wm.trigger !== TRIGGERS.onExit)
    .filter(wm => !wm.default)
    .filter(wm => isWelcomeMessageSuitable(wm, url, device))

  for (const welcomeMessageId of alreadySeenWelcomeMessageIds()) {
    const welcomeMessage = availableWelcomeMessages.find(wm => wm.id.toString() === welcomeMessageId)
    if (welcomeMessage) return welcomeMessage
  }

  return null
}

// only exported for spec
export function getAbTestedWelcomeMessage (welcomeMessages: Array<WelcomeMessage>, url: string = '', device: DeviceType): ?WelcomeMessage {
  const welcomeMessagesForAbTest = welcomeMessages
    .filter(w => w.trigger === TRIGGERS.immediately || w.trigger === TRIGGERS.timePassed || w.trigger === TRIGGERS.percentScrolled)
    .filter(w => !w.default)
    .filter(w => isWelcomeMessageSuitable(w, url, device))

  if (welcomeMessagesForAbTest.length === 0) return

  const randomIndex = Math.floor(Math.random() * welcomeMessagesForAbTest.length)
  return welcomeMessagesForAbTest[randomIndex]
}

// only exported for spec
export function getDefaultWelcomeMessage (welcomeMessages: Array<WelcomeMessage>): ?WelcomeMessage {
  return welcomeMessages.find(w => w.default === true && !(w.type === 'open_chat'))
}

export function getValidExitWelcomeMessage (welcomeMessages: Array<WelcomeMessage>, url: string): ?WelcomeMessage {
  return welcomeMessages.find(wm => wm.trigger === TRIGGERS.onExit && wm.active && (!wm.regex || isUrlMatchingWrapper(wm, url)))
}

function isUrlMatchingWrapper (wm: WelcomeMessage, url: string): boolean {
  const urlMatchSettings = {
    regex: wm.regex ?? '',
    pathMatcher: wm.pathMatcher ?? 'regex'
  }

  return isUrlMatching(urlMatchSettings, url, wm.properties.regexApplyToSearchQuery)
}
